import * as React from "react";

import { DashboardType } from "@app/user/enumerations";
import { GuestLoginInfo } from "@app/user/guestLoginInfo";

/** The currently authenticated user. */
interface UserInfo {
  /** The user's unique identifier. */
  userId?: number;

  /** The user's display name. Typically their full name. */
  displayName: string;

  /** The user's username. Typically their email. */
  name: string;

  /** The set of roles the user has been assigned. */
  roles: string[];

  /** The number of seconds from UNIX epoch. */
  expiration: number;

  /** The types of dashboards the user is authorized to access. */
  authorizedDashboardTypes: DashboardType[];

  /** If true, the user is authorized to download the desktop software. */
  canDownload: boolean;

  /** The time maintenance will be conducted at. */
  maintenanceTime: string;

  /** If true, the user gets an alert warning them of upcoming maintenance. */
  showMaintenanceAlert: boolean;

  /** If true, the user is displayed a maintenance page rather than being able to interact with the application.  */
  showMaintenancePage: boolean;

  /** The email address for the user, */
  email: string;

  /** The first name for the user. */
  firstName: string;

  /** The middle initial for the user. */
  middleInitial: string;

  /** The last namefor the user. */
  lastName: string;

  /** The address for the user. */
  address: string;

  /** The city for the user. */
  city: string;

  /** The state for the user. */
  state: string;

  /** The zip for the user. */
  zip: string;

  /** The phone numberfor the user. */
  phoneNumber: string;

  /** The guest login info to which the user is associated */
  guestLoginInfo: GuestLoginInfo;

  /** Whether or not this is the primary account for the user */
  readonly isPrimaryAccount: boolean;

  /** the client id for the user */
  clientId: number;

  /** The client name for the user */
  clientName: string;
}

interface UserContextProps {
  user: UserInfo;
}

/** Handles the manipulation and retrieval of the currently authenticated user. */
const UserContext = React.createContext<UserInfo>(null);

function withUserContext<T>(Component: React.ComponentType<React.PropsWithChildren<T & UserContextProps>>): React.ComponentType<React.PropsWithChildren<T>> {
  class WithUserContext extends React.PureComponent<T> {
    public render() {
      return (
        <UserContext.Consumer>
          {(user) => <Component {...this.props} {...{ user } as UserContextProps} />}
        </UserContext.Consumer>
      );
    }
  }

  return WithUserContext;
}

export {
  UserContext,
  UserContextProps,
  UserInfo,
  withUserContext,
};
